import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { type MatchSummaryDTO, deleteMatch, getSessionResults } from "../api";

// Results recorded for one session. Admins can undo a result (which recomputes
// ratings). There's no in-place edit — to fix a score, undo it and re-record.
export function SessionResultsModal({
  groupId,
  sessionId,
  onClose,
}: {
  groupId: string;
  sessionId: string;
  onClose: (changed: boolean) => void;
}) {
  const [matches, setMatches] = useState<MatchSummaryDTO[] | null>(null);
  const [canDelete, setCanDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getSessionResults(sessionId)
      .then((d) => {
        if (cancelled) return;
        setMatches(d.matches);
        setCanDelete(d.can_delete);
      })
      .catch((e) => !cancelled && setError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  async function undo(matchId: string) {
    setBusyId(matchId);
    setError(null);
    try {
      await deleteMatch(groupId, matchId);
      setMatches((ms) => (ms ?? []).filter((m) => m.id !== matchId));
      setChanged(true);
    } catch (e) {
      setError(String((e as Error)?.message ?? e));
    } finally {
      setBusyId(null);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-ink/55 cs-modal-overlay" onClick={() => onClose(changed)} />
      <div className="absolute bottom-0 left-0 right-0 cs-modal-card sm:top-1/2 sm:bottom-auto sm:-translate-y-1/2">
        <div className="max-w-lg mx-auto px-4 pb-4" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
          <div className="bg-bg border-2 border-ink rounded-t-xl sm:rounded-xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
            <div className="px-5 py-3 bg-ink text-bg flex items-center justify-between shrink-0">
              <div className="font-mono text-[11px] uppercase tracking-[0.16em]">Results</div>
              <button
                onClick={() => onClose(changed)}
                className="text-bg/60 hover:text-bg font-mono text-2xl leading-none ml-3"
              >
                ×
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto">
              {error && (
                <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                  {error}
                </div>
              )}
              {matches == null && !error ? (
                <div className="text-ink/55 text-sm py-6 text-center">Loading…</div>
              ) : (matches?.length ?? 0) === 0 ? (
                <div className="text-ink/55 text-sm py-6 text-center">
                  No results recorded for this session yet.
                </div>
              ) : (
                <ul className="flex flex-col gap-1">
                  {matches!.map((m) => {
                    const winA = m.winner === "a";
                    return (
                      <li
                        key={m.id}
                        className="flex items-center gap-2 px-3 py-2 border border-ink/10 rounded-md text-sm"
                      >
                        <span className="flex-1 truncate">
                          <span className={winA ? "font-bold" : "text-ink/55"}>
                            {m.team_a.join(" & ")}
                          </span>
                          <span className="text-ink/35"> vs </span>
                          <span className={!winA ? "font-bold" : "text-ink/55"}>
                            {m.team_b.join(" & ")}
                          </span>
                          {m.score && (
                            <span className="text-ink/45 font-mono text-xs"> · {m.score}</span>
                          )}
                        </span>
                        {canDelete && (
                          <button
                            onClick={() => void undo(m.id)}
                            disabled={busyId === m.id}
                            title="Undo this result"
                            className="shrink-0 font-mono text-[10px] uppercase tracking-wider border border-ink/20 hover:border-[#C84B31] hover:text-[#C84B31] px-2.5 py-1 cs-tap disabled:opacity-40"
                          >
                            {busyId === m.id ? "…" : "Undo"}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
